import { useEffect, useRef } from 'react';
import { Animated } from 'react-native';

// Effort-meter reminder pulse (AP-0d; retimed AP-0i, reworked AP-0l per
// Antenore's smoke; backed off AP-0q). While a pending set sits unlogged, the
// meter softly asks for its tap. The FIRST cue fires ~1s after the set binds
// to the card; the SECOND follows ~9s later. After that it backs off —
// AP-0q ("il cue insiste troppo"): each further wait DOUBLES (9s → 18s → 36s
// → 72s → 144s → …), capped around 180s, so a set ignored for minutes stops
// being re-asked every 9s forever without ever going silent. Each cue is ONE
// slow cycle — fade in → hold → fade out — so the text is readable and the
// frame breathes (was a fast double-blink that vanished before you could read
// it). The whole cadence resets to its start whenever the binding moves (a
// new `setKey` — the next set begins clean, not mid-backoff) or the set
// completes. Returns the opacity — the host drives BOTH a brand frame around
// the scale AND a "tap to log" pill with it.
//
// Reduce-motion drops the fade easing (instant on/off) but keeps the same
// appear/clear cadence — it does NOT hold the pill static on-screen. That is
// deliberate: a pill held permanently over the band covers the ramp and leaves
// it readable by colour alone ([[feedback_instructional_overlay_pulses]]).
// Reduced motion here means no continuous animation, not a frozen overlay.

const FIRST_CUE_MS = 1_000;
const NUDGE_EVERY_MS = 9_000;
// AP-0q backoff cap — the wait between cues never grows past this, so an
// ignored set is still nudged occasionally rather than going silent forever.
const NUDGE_MAX_MS = 180_000;
const STATIC_CUE_MS = 2_500;
// Peak = FULL opacity: drives the frame + the readable text pill. A sub-1 peak
// would sink the brandText cue below the AA floor (a11y audit AP-0i); the frame
// is a border, so full opacity there never hides the scale. "Transparent enough
// to see the scale" (Antenore AP-0l) comes from the border shape + the fade-out
// between cues, not from a faint peak.
const CUE_PEAK = 1;

export function useEffortNudge(args: {
  /** True while a PENDING set is bound (the only state worth nudging). */
  active: boolean;
  /** Identity of the bound set — a change resets the cadence. */
  setKey: string;
  /** Settings toggle (effortNudgePreference). */
  enabled: boolean;
  /** OS reduce-motion (useReducedMotion) → static cue, no blink. */
  reducedMotion: boolean;
}) {
  const { active, setKey, enabled, reducedMotion } = args;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!active || !enabled) {
      opacity.setValue(0);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    let staticTimer: ReturnType<typeof setTimeout> | null = null;
    // AP-0q: the gap before the NEXT cue, doubling after every firing up to
    // NUDGE_MAX_MS. A `let` closed over by scheduleNext, not a ref — it lives
    // and dies with this effect, so a `setKey` change (new dependency array
    // identity → cleanup → re-run) throws it away and starts a fresh one at
    // NUDGE_EVERY_MS, exactly the reset the task asks for.
    let nextDelay = NUDGE_EVERY_MS;
    const pulse = () => {
      if (reducedMotion) {
        if (staticTimer) clearTimeout(staticTimer);
        opacity.setValue(CUE_PEAK);
        staticTimer = setTimeout(() => opacity.setValue(0), STATIC_CUE_MS);
        return;
      }
      // One slow, readable cycle: ease up → hold → ease down (was a fast
      // double-blink that vanished before you could read the pill).
      Animated.sequence([
        Animated.timing(opacity, { toValue: CUE_PEAK, duration: 600, useNativeDriver: true }),
        Animated.delay(1_100),
        Animated.timing(opacity, { toValue: 0, duration: 700, useNativeDriver: true }),
      ]).start();
    };
    // AP-0q: reschedule itself after every firing instead of a fixed
    // setInterval, doubling the wait each time (capped at NUDGE_MAX_MS) — a
    // setInterval can only repeat at one fixed cadence, so backoff needs a
    // recursive setTimeout instead. It stops for good the moment the set is
    // logged, when `active` flips false and the effect tears down.
    const scheduleNext = () => {
      timer = setTimeout(() => {
        pulse();
        nextDelay = Math.min(nextDelay * 2, NUDGE_MAX_MS);
        scheduleNext();
      }, nextDelay);
    };
    // First cue ~1s after the set binds, then the backoff cadence above.
    const firstTimer = setTimeout(() => {
      pulse();
      scheduleNext();
    }, FIRST_CUE_MS);
    return () => {
      clearTimeout(firstTimer);
      if (timer) clearTimeout(timer);
      if (staticTimer) clearTimeout(staticTimer);
      opacity.stopAnimation(() => opacity.setValue(0));
    };
  }, [active, setKey, enabled, reducedMotion, opacity]);

  return opacity;
}
